import {useMemo, useState} from "react";
import {OperatingAreaEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";

export type UseOperatingAreaDataTableFilterProps = Omit<UseDataTableFilterProps<OperatingAreaEntity>, 'filterConfig'>

export function useOperatingAreaDataTableFilter(props: UseOperatingAreaDataTableFilterProps): UseDataTableFilterResult<OperatingAreaEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [companyQuery, setCompanyQuery] = useState<string | undefined>(undefined);
  const [companyDebouncedQuery] = useDebouncedValue(companyQuery, 200);

  const [companyUnitQuery, setCompanyUnitQuery] = useState<string | undefined>(undefined);
  const [companyUnitDebouncedQuery] = useDebouncedValue(companyUnitQuery, 200);

  const filterConfig: DataTableFilterConfig<OperatingAreaEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: (record, query) => !record.name.toLowerCase().includes(query.toLowerCase())
      },
      "companyName": {
        setQuery: setCompanyQuery,
        query: companyQuery,
        debouncedQuery: companyDebouncedQuery,
        filter: ({companyName}, query) => !companyName || !companyName.toLowerCase().includes(query.toLowerCase())
      },
      "companyUnitName": {
        setQuery: setCompanyUnitQuery,
        query: companyUnitQuery,
        debouncedQuery: companyUnitDebouncedQuery,
        filter: ({companyUnitName}, query) => !companyUnitName || !companyUnitName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    companyQuery,
    companyDebouncedQuery,
    companyUnitQuery,
    companyUnitDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
